import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  FormHelperText,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';

// Solo como TIPO, para no arrastrar `class-validator` al navegador.
import type { VoucherLogoDto } from '@bandido/shared/dtos';
import { UploadButton } from '@frontend/features/common/components/UploadButton';

import {
  VOUCHER_IDENTITY_FIELDS,
  VoucherIdentityDraft,
  VoucherIdentityField,
  voucherHeaderColorEnabled,
  voucherIdentityMaxLength,
  voucherToggleHeaderColor,
} from '../voucherIdentity';

const KEY = 'vouchers.config';

interface LogoSlotProps {
  kind: 'primary' | 'negative';
  logo?: VoucherLogoDto | null;
  disabled: boolean;
  /** Por qué está apagado, si lo está: no es lo mismo "no sos admin" que
   * "todavía no se leyó la configuración", y decir lo primero cuando pasa lo
   * segundo manda al asesor a pedir permisos que ya tiene. */
  disabledReason?: string;
  onPick: (file: File) => void;
}

/**
 * Un logo. Se sube al ELEGIRLO —el endpoint es aparte del Guardar— y la
 * pantalla lo dice, para que nadie crea que se pierde si no guarda.
 */
const LogoSlot: FC<LogoSlotProps> = ({
  kind,
  logo,
  disabled,
  disabledReason,
  onPick,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <Box display="flex" flexDirection="column" gap={1} flex={1} minWidth={240}>
      <Typography variant="subtitle2">{t(`${KEY}.logo.${kind}`)}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t(`${KEY}.logo.${kind}Hint`)}
      </Typography>
      <Box
        sx={{
          border: `1px dashed ${theme.palette.divider}`,
          borderRadius: '12px',
          padding: theme.spacing(2),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 96,
          // El negativo va sobre el color de cabecera: sobre blanco no se ve.
          backgroundColor:
            kind === 'negative' ? theme.palette.grey[800] : 'transparent',
        }}
      >
        {logo ? (
          <img
            src={logo.url}
            alt={logo.filename}
            style={{ maxHeight: 72, maxWidth: '100%' }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t(`${KEY}.logo.empty`)}
          </Typography>
        )}
      </Box>
      {disabled ? (
        // Sin permiso el control se ve apagado y NO abre el diálogo: dejarlo
        // vivo y descartar el archivo en silencio es peor que no ofrecerlo.
        <Button variant="outlined" startIcon={<UploadFileIcon />} disabled>
          {t(`${KEY}.logo.upload`)}
        </Button>
      ) : (
        <UploadButton
          // `component="label"` es lo que hace que el clic llegue al input:
          // como <button> el input queda adentro y no se activa nunca, así que
          // el botón no abría nada. Es como lo usa el resto del repo.
          component="label"
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onChange={(event) => {
            const file = event.target.files?.[0];
            // El input se limpia para que elegir el MISMO archivo otra vez
            // —después de un rechazo— vuelva a disparar el cambio.
            event.target.value = '';
            if (file) onPick(file);
          }}
        >
          {t(`${KEY}.logo.upload`)}
        </UploadButton>
      )}
      {disabled && disabledReason && (
        <FormHelperText>{t(disabledReason)}</FormHelperText>
      )}
    </Box>
  );
};

interface IdentitySectionProps {
  draft: VoucherIdentityDraft;
  issues: Partial<Record<keyof VoucherIdentityDraft, string>>;
  logo?: VoucherLogoDto | null;
  logoNegative?: VoucherLogoDto | null;
  canEdit: boolean;
  /** El color que la agencia tiene guardado, para restaurarlo al reprender. */
  savedHeaderColor: string;
  /** El motivo por el que no se puede editar, cuando no se puede. */
  disabledReason?: string;
  logoError?: { message?: string; key: string; params?: Record<string, number> };
  onChange: (field: keyof VoucherIdentityDraft, value: string) => void;
  onPickLogo: (kind: 'primary' | 'negative', file: File) => void;
}

/**
 * IDENTIDAD: lo que sale impreso en TODOS los documentos de la agencia. La
 * pantalla no valida por su cuenta —los topes y las formas salen del DTO, vía
 * `voucherIdentity.ts`—, solo muestra lo que ese módulo decide.
 */
export const IdentitySection: FC<IdentitySectionProps> = ({
  draft,
  issues,
  logo,
  logoNegative,
  canEdit,
  savedHeaderColor,
  disabledReason,
  logoError,
  onChange,
  onPickLogo,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const usaColor = voucherHeaderColorEnabled(draft.headerColor);
  return (
    <Stack gap={3}>
      <Alert severity="info">{t(`${KEY}.identity.appliesToAll`)}</Alert>

      <Box display="flex" gap={3} flexWrap="wrap">
        <LogoSlot
          kind="primary"
          logo={logo}
          disabled={!canEdit}
          disabledReason={disabledReason}
          onPick={(file) => onPickLogo('primary', file)}
        />
        <LogoSlot
          kind="negative"
          logo={logoNegative}
          disabled={!canEdit}
          disabledReason={disabledReason}
          onPick={(file) => onPickLogo('negative', file)}
        />
      </Box>
      {logoError && (
        <Alert severity="error">
          {logoError.message ?? t(logoError.key, logoError.params)}
        </Alert>
      )}

      <Box
        display="grid"
        gap={2}
        gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
      >
        {VOUCHER_IDENTITY_FIELDS.map((field: VoucherIdentityField) => (
          <TextField
            key={field}
            label={t(`${KEY}.identity.${field}`)}
            value={draft[field]}
            disabled={!canEdit}
            error={Boolean(issues[field])}
            helperText={
              issues[field]
                ? t(issues[field] as string, {
                  max: voucherIdentityMaxLength(field),
                })
                : t(`${KEY}.identity.${field}Hint`)
            }
            // El tope lo pone el DTO: el navegador corta donde corta el borde.
            inputProps={{ maxLength: voucherIdentityMaxLength(field) }}
            onChange={(event) => onChange(field, event.target.value)}
            fullWidth
            size="small"
          />
        ))}
      </Box>

      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <Box>
          <Typography variant="subtitle2">
            {t(`${KEY}.identity.headerColor`)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(`${KEY}.identity.headerColorHint`)}
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={usaColor}
              disabled={!canEdit}
              onChange={(event) =>
                onChange(
                  'headerColor',
                  voucherToggleHeaderColor(
                    draft.headerColor,
                    event.target.checked,
                    // El anterior es el GUARDADO: una memoria de la pantalla
                    // se pierde al cambiar de solapa y volvía con otro color.
                    savedHeaderColor,
                  ),
                )
              }
            />
          }
          label={t(`${KEY}.identity.headerColorEnabled`)}
        />
        <input
          type="color"
          aria-label={t(`${KEY}.identity.headerColor`)}
          // Apagado no se puede tocar: el selector nativo no sabe representar
          // "sin color" y muestra blanco, así que abrirlo y aceptar convertía
          // "sin color" en "blanco" —que NO es lo mismo: con color el motor
          // imprime el logo negativo, y sobre blanco no se ve—.
          disabled={!canEdit || !usaColor}
          value={usaColor ? draft.headerColor : '#ffffff'}
          onChange={(event) => onChange('headerColor', event.target.value)}
          style={{
            width: 56,
            height: 40,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 8,
            background: 'none',
            opacity: usaColor ? 1 : 0.4,
          }}
        />
        <TextField
          value={draft.headerColor}
          disabled={!canEdit || !usaColor}
          error={Boolean(issues.headerColor)}
          helperText={
            issues.headerColor
              ? t(issues.headerColor)
              : t(
                usaColor
                  ? `${KEY}.identity.headerColorOn`
                  : `${KEY}.identity.headerColorOff`,
              )
          }
          onChange={(event) => onChange('headerColor', event.target.value)}
          size="small"
          sx={{ width: 160 }}
        />
      </Box>
    </Stack>
  );
};
